import React, { useEffect } from 'react';

import { useForm } from 'react-hook-form';
import { enqueueSnackbar } from 'notistack';
import { useAsyncFn } from 'react-use';

import { LbModal } from '../../../../shared/components/modals';
import {
  ComboBox,
  OptionWithDescription,
} from '../../../../shared/components/form-components';
import { CustomerType } from '../../../../shared/constants/customer/customerType';
import { getScanService } from '../../../services/scanService/getScanService';
import { ScanMetadata } from '../../../interfaces/scanMetadata';
import { useScansMetadata } from '../../../hooks/useScansMetadata/useScansMetadata';
import { CustomerInfo } from '../../../pages/customer-profile/containers/NgzInbodyIndicatorsTab/NgzInbodyIndicatorsTab.settings';
import { getInbodyResultFileName } from './utils/getInbodyResultFileName';
import inbodyResultSheetImg from '../../../../shared/assets/image/inbody-result-sheet.png';
import classes from './InbodyResultSheetModal.module.scss';
import { getIsScanDisabled } from './utils/getIsScanDisabeld';
import { useScanDateOptions } from '../../../hooks/useScanDateOptions/useScanDateOptions';

type InbodyResultSheetFormValues = {
  scanId: number | null;
};

type InbodyResultSheetModalProps = {
  customerType: CustomerType;
  customerId: number;
  customerName: Pick<CustomerInfo, 'firstName' | 'lastName'>;
  entityId?: number;
  scansMetadata?: ScanMetadata[];
  onClose: () => void;
};

const InbodyResultSheetModal = ({
  customerType,
  customerId,
  customerName,
  entityId,
  scansMetadata: scansMetadataProp,
  onClose,
}: InbodyResultSheetModalProps) => {
  const { scansMetadata, isLoading } = useScansMetadata({
    customerType,
    customerId,
    entityId,
    scansMetadata: scansMetadataProp,
  });

  const latestAvailableScanId =
    scansMetadata.find((scan) => !getIsScanDisabled(scan))?.scanId ?? null;

  const { control, handleSubmit, reset } = useForm<InbodyResultSheetFormValues>({
    defaultValues: { scanId: latestAvailableScanId },
  });

  // fetched scans arrive after mount, so the default pick has to be re-applied
  useEffect(() => {
    reset({ scanId: latestAvailableScanId });
  }, [latestAvailableScanId]);

  const scanService = getScanService(customerType);

  const scanOptions = useScanDateOptions(scansMetadata);

  const handleDownloadResultSheet = async ({ scanId }: InbodyResultSheetFormValues) => {
    if (!scanId) {
      enqueueSnackbar('Scan date is required', { variant: 'error' });
      return;
    }

    const fileName = getInbodyResultFileName(scansMetadata, scanId, customerName);
    await scanService.downloadInbodyResultSheet({
      customerId,
      scanId,
      fileName,
      entityId,
    });

    enqueueSnackbar('Your file has been generated and downloaded successfully.', {
      variant: 'success',
      autoHideDuration: 3000,
    });
    onClose();
  };

  const [{ loading: isDownloading }, downloadResultSheet] = useAsyncFn(
    handleDownloadResultSheet,
    [scansMetadata, customerName, customerId, entityId, scanService, onClose],
  );

  return (
    <LbModal
      open
      title="InBody Result Sheet"
      text="Detailed body composition measurements from an InBody scan."
      isLoading={isLoading || isDownloading}
      onClose={onClose}
      primaryBtnConfig={{
        text: 'Generate and Download',
        onClick: handleSubmit(downloadResultSheet),
        disabled: isLoading || !scansMetadata.length,
      }}
      secondaryBtnConfig={{ onClick: onClose }}
    >
      <div className={classes.content}>
        <img
          src={inbodyResultSheetImg}
          alt="InBody result sheet preview"
          className={classes.preview}
        />
        <ComboBox
          control={control}
          name="scanId"
          options={scanOptions}
          rules={{ required: 'Scan date is required' }}
          variant="outlined"
          size="small"
          valueKey="scanId"
          getOptionDisabled={getIsScanDisabled}
          renderOptionLabel={(option) => {
            return (
              <OptionWithDescription
                label={option.label}
                description={option.description}
              />
            );
          }}
        />
      </div>
    </LbModal>
  );
};

export { InbodyResultSheetModal };
